import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Router} from '@angular/router';
import {SelectionItem} from '../selections/selection-item';
import {PersonLoginService} from '../services/person-login-service';
import {UserApplicationService} from '../services/user-application-service';
import {StringUtils} from '../utils/string-utils';
import {UserApplication} from './user-application';
import {UserApplicationData} from './user-application-data';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

@Component({
    selector: 'user-application',
    templateUrl: './user-application.component.html',
    styleUrls: ['./user-application.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, NgClass, MatProgressSpinner]
})
export class UserApplicationComponent implements OnInit {
  private readonly userApplicationService = inject(UserApplicationService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly router = inject(Router);


  readonly isBusy = signal<boolean>(false);
  readonly applicationExists = signal<boolean>(false);
  defaultTitle!: SelectionItem;
  defaultCompany!: SelectionItem;
  userApplication!: UserApplication;
  readonly applicationData = signal<UserApplicationData>(undefined!);
  readonly errorMessage = signal<string>(undefined!);

  ngOnInit(): void {
    this.isBusy.set(true);
    this.createDefaults();
    const credential = this.personLoginService.getApplicationCredential();
    if (!credential || credential.trim().length === 0) {
      this.router.navigate(['/']);
    } else {
      this.getExistingApplication(credential);
    }
  }

  createDefaults(): void {
    this.defaultTitle = new SelectionItem();
    this.defaultCompany = new SelectionItem();
    this.defaultTitle.description = "Select Title";
    this.defaultTitle.code = "default";
    this.defaultCompany.description = "Select Company";
    this.defaultCompany.code = "default"
  }

  getExistingApplication(credential: string): void {
    this.userApplicationService.getExistingApplication(credential).subscribe({
      next: result => {
        this.isBusy.set(false);
        if (result) {
          this.applicationExists.set(true);
        } else {
          this.userApplication = new UserApplication();
          this.userApplication.userName = credential;
        }
      }, error: error => this.handleError(error)
    });
  }

  getApplicationData(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.userApplicationService.getEmailCompanyData(this.userApplication).subscribe({
      next: result => {
        this.applicationData.set(result);
        this.applicationData().jobTitles.unshift(this.defaultTitle);
        this.applicationData().companies = [this.defaultCompany];
        this.userApplication.jobTitle = this.defaultTitle.code;
        this.userApplication.company = this.defaultCompany.code;
        this.isBusy.set(false);
      }
      , error: error => {
        this.handleError(error)
      }
    });
  }

  updateApplicationData(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.userApplicationService.getEmailCompanyData(this.userApplication).subscribe({
      next: result => {
        this.applicationData().companies = result.companies
        if (this.applicationData().companies.length > 1) {
          this.applicationData().companies.unshift(this.defaultCompany);
        }
        this.userApplication.company = result.companies[0].code;
        this.isBusy.set(false);
      }
      , error: error => {
        this.handleError(error)
      }
    });
  }

  jobTitleChanged(): void {
    if (this.userApplication.jobTitle === this.defaultTitle.code) {
      this.applicationData().companies = [this.defaultCompany];
    } else {
      this.updateApplicationData();
    }
  }

  submitDisabled(): boolean {
    return this.isBusy() || this.userApplication.company === this.defaultCompany.code || this.userApplication.jobTitle === this.defaultTitle.code;
  }

  startOver(): void {
    this.errorMessage.set(undefined!);
    this.applicationData.set(undefined!);
    this.userApplication.workEmail = "";
  }

  submitApplication(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.userApplicationService.save(this.userApplication).subscribe({
      next: () => {
        this.applicationData.set(undefined!);
        this.userApplication = null!;
        this.applicationExists.set(true);
        this.isBusy.set(false);
      },
      error: error => this.handleError(error)
    });
  }

  private handleError(error: HttpErrorResponse) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

}
